import { useMemo, useRef, useState } from 'react'
import {
  ArrowLeft,
  ArrowRight,
  Browser,
  CalendarCheck,
  Check,
  Code,
  DeviceMobile,
  EnvelopeSimple,
  FlowArrow,
  Gauge,
  PaperPlaneTilt,
  Robot,
  ShoppingBag,
  Sparkle,
  UserCircle,
} from '@phosphor-icons/react'

const BOT_API_URL = (import.meta.env.VITE_BOT_API_URL || '').replace(/\/$/, '')
const OFF_TOPIC_REPLY = 'Я отвечаю только по услугам Kazak.V: сайты, HTML-письма, Telegram-боты и Mini Apps.'

const siteFormats = [
  {
    title: 'Лендинг',
    text: 'Одна сфокусированная страница для запуска услуги, продукта, события или рекламной кампании.',
    result: 'Понятный оффер, аргументы, ответы на вопросы и целевое действие.',
  },
  {
    title: 'Корпоративный сайт',
    text: 'Несколько разделов о компании, услугах, команде, кейсах и условиях работы.',
    result: 'Структура, которую можно развивать вместе с бизнесом.',
  },
  {
    title: 'Каталог или портфолио',
    text: 'Проекты, товары или услуги с фильтрами, отдельными страницами и удобной навигацией.',
    result: 'Посетителю легко сравнить варианты и выбрать подходящий.',
  },
]

const siteDeliverables = [
  'Смысловая структура и прототип страниц',
  'Тексты или редактура материалов заказчика',
  'Адаптивная версия для телефона и компьютера',
  'Формы, аналитика и необходимые интеграции',
  'Базовая техническая SEO-подготовка',
  'Проверка перед запуском и инструкция по работе',
]

const emailFormats = [
  ['Промо-рассылки', 'Акция, запуск продукта, подборка предложений или персональная рекомендация.'],
  ['Регулярные дайджесты', 'Новости, статьи, обновления продукта и контент в повторяемом шаблоне.'],
  ['Транзакционные письма', 'Заказ, оплата, доставка, восстановление доступа и другие системные события.'],
  ['Welcome-цепочки', 'Серия писем, которая знакомит нового подписчика с продуктом и ведёт к следующему шагу.'],
]

const miniApps = [
  {
    icon: CalendarCheck,
    title: 'Запись и бронирование',
    text: 'Выбор услуги, специалиста и времени без перехода из Telegram.',
    features: ['календарь', 'уведомления', 'оплата или предоплата'],
  },
  {
    icon: ShoppingBag,
    title: 'Каталог и заказ',
    text: 'Витрина товаров или услуг с карточками, корзиной и оформлением заказа.',
    features: ['поиск и фильтры', 'корзина', 'статус заказа'],
  },
  {
    icon: UserCircle,
    title: 'Личный кабинет',
    text: 'Доступ к подписке, документам, балансу, истории обращений или этапам проекта.',
    features: ['профиль', 'история', 'закрытые материалы'],
  },
  {
    icon: Sparkle,
    title: 'Лояльность и рефералы',
    text: 'Бонусы, уровни, промокоды и приглашения друзей внутри привычного интерфейса.',
    features: ['баллы', 'реферальные ссылки', 'персональные акции'],
  },
]

function ServicePageShell({ service, onBack, eyebrow, title, intro, aside, children }) {
  return (
    <main className={`service-page service-${service.id}-page`} style={{ '--service-color': service.color }}>
      <header className="service-page-header">
        <button type="button" className="service-back" onClick={onBack}>
          <ArrowLeft weight="bold" aria-hidden="true" />
          Все услуги
        </button>
        <a className="service-brand" href="#top" aria-label="В начало страницы">
          <img src="/assets/kazak-z-logo.png" alt="" />
          <span>Kazak.V</span>
        </a>
        <span className="service-counter">{service.number} / 03</span>
      </header>

      <section className="service-hero" id="top" aria-labelledby="service-title">
        <div className="service-hero-copy">
          <p className="service-kicker">{eyebrow}</p>
          <h1 id="service-title">{title}</h1>
          <p className="service-lead">{intro}</p>
          <a className="service-primary-link" href="#service-content">
            Посмотреть содержание <ArrowRight weight="bold" aria-hidden="true" />
          </a>
        </div>
        {aside}
      </section>

      <div id="service-content" className="service-page-content">
        {children}
      </div>
    </main>
  )
}

function SectionHeading({ number, label, title, text }) {
  return (
    <header className="section-heading">
      <span>{number} / {label}</span>
      <div>
        <h2>{title}</h2>
        {text ? <p>{text}</p> : null}
      </div>
    </header>
  )
}

function SitesPage({ service, onBack, onNavigate }) {
  return (
    <ServicePageShell
      service={service}
      onBack={onBack}
      eyebrow="Разработка сайтов"
      title={<>Сайт, который <em>объясняет</em> и ведёт к заявке</>}
      intro="Помогаю превратить услугу или продукт в понятную структуру, собрать адаптивный сайт и подготовить его к запуску. Сейчас эта страница показывает состав будущего лендинга, без финального дизайна."
      aside={(
        <aside className="hero-summary">
          <span>Подходит для</span>
          <strong>услуг, экспертов, студий, небольших компаний и новых продуктов</strong>
          <small>От первого прототипа до опубликованного сайта</small>
        </aside>
      )}
    >
      <section className="content-section">
        <SectionHeading
          number="01"
          label="Форматы"
          title="Что можно разработать"
          text="Формат выбирается не по моде, а по задаче, объёму информации и пути клиента."
        />
        <div className="content-card-grid content-card-grid-three">
          {siteFormats.map((item) => (
            <article className="content-card" key={item.title}>
              <Browser aria-hidden="true" />
              <h3>{item.title}</h3>
              <p>{item.text}</p>
              <small>{item.result}</small>
            </article>
          ))}
        </div>
      </section>

      <section className="content-section split-section">
        <SectionHeading
          number="02"
          label="Результат"
          title="Что входит в работу"
          text="Не только вёрстка экрана, а весь минимально необходимый путь до рабочего запуска."
        />
        <ul className="check-list">
          {siteDeliverables.map((item) => (
            <li key={item}><Check weight="bold" aria-hidden="true" />{item}</li>
          ))}
        </ul>
      </section>

      <section className="content-section">
        <SectionHeading number="03" label="Процесс" title="Как строится проект" />
        <ol className="process-list">
          {[
            ['Знакомство', 'Разбираем продукт, аудиторию, цель сайта и ограничения.'],
            ['Структура', 'Собираем страницы, смысловые блоки и сценарий посетителя.'],
            ['Прототип', 'Проверяем порядок информации до начала визуального дизайна.'],
            ['Дизайн', 'Создаём индивидуальную систему экранов и компонентов.'],
            ['Разработка', 'Верстаем, подключаем формы, аналитику и интеграции.'],
            ['Запуск', 'Тестируем, публикуем и передаём понятную инструкцию.'],
          ].map(([title, text], index) => (
            <li key={title}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <div><h3>{title}</h3><p>{text}</p></div>
            </li>
          ))}
        </ol>
      </section>

      <section className="content-section content-note">
        <Code aria-hidden="true" />
        <div>
          <span>Что понадобится для оценки</span>
          <h2>Коротко опишите задачу, продукт, нужные страницы и желаемый срок.</h2>
          <p>Если точного технического задания нет, это нормально. Его можно собрать вместе после первого разговора.</p>
        </div>
        <button type="button" onClick={() => onNavigate('bots')}>Спросить консультанта</button>
      </section>

      <section className="content-section faq-section">
        <SectionHeading number="04" label="Вопросы" title="Что обычно уточняют" />
        <div>
          <details><summary>Можно начать только с прототипа?</summary><p>Да. Смысловую структуру можно сделать отдельным этапом, а дизайн и разработку продолжить после её утверждения.</p></details>
          <details><summary>Сайт будет работать на телефоне?</summary><p>Да. Мобильная версия входит в работу и проверяется отдельно, а не уменьшается механически из компьютерной.</p></details>
          <details><summary>Можно подключить домен, формы и аналитику?</summary><p>Да. Набор подключений определяется задачей: формы, уведомления, аналитика, CRM, оплата или другие сервисы.</p></details>
        </div>
      </section>
    </ServicePageShell>
  )
}

function EmailsPage({ service, onBack, onNavigate }) {
  return (
    <ServicePageShell
      service={service}
      onBack={onBack}
      eyebrow="Дизайн и HTML-вёрстка писем"
      title={<>Письма, которые удобно <em>читать и нажимать</em></>}
      intro="Проектирую структуру письма, оформляю её под бренд и собираю адаптивный HTML-шаблон для рассылки. Страница ниже показывает, какое содержание стоит оставить в финальной версии услуги."
      aside={(
        <aside className="email-preview" aria-label="Схема HTML-письма">
          <div className="email-preview-bar"><i /><i /><i /><span>Новое письмо</span></div>
          <strong>Один главный смысл</strong>
          <p>Короткое объяснение пользы и заметная кнопка действия.</p>
          <span>Открыть предложение</span>
        </aside>
      )}
    >
      <section className="content-section">
        <SectionHeading
          number="01"
          label="Форматы"
          title="Какие письма можно сделать"
          text="Шаблон собирается под конкретный сценарий, а не как универсальный красивый макет."
        />
        <div className="email-format-list">
          {emailFormats.map(([title, text], index) => (
            <article key={title}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="content-section split-section">
        <SectionHeading
          number="02"
          label="Состав"
          title="Что получает заказчик"
          text="Результат можно передать специалисту по рассылкам или загрузить в выбранный сервис."
        />
        <ul className="check-list">
          {[
            'Структура письма и расстановка приоритетов',
            'Дизайн в визуальном стиле бренда',
            'Адаптивная HTML-вёрстка',
            'Корректные ссылки и кнопки',
            'Проверка основных почтовых сценариев',
            'Файлы и пояснения для загрузки в сервис рассылок',
          ].map((item) => <li key={item}><Check weight="bold" aria-hidden="true" />{item}</li>)}
        </ul>
      </section>

      <section className="content-section email-anatomy-section">
        <SectionHeading number="03" label="Структура" title="Из чего складывается понятное письмо" />
        <div className="email-anatomy">
          <div><span>01</span><strong>Тема и прехедер</strong><p>Обещают понятную пользу без обмана и лишнего шума.</p></div>
          <div><span>02</span><strong>Первый экран</strong><p>Сразу объясняет, зачем человеку читать дальше.</p></div>
          <div><span>03</span><strong>Основная часть</strong><p>Аргументы идут в удобном порядке и поддерживают одну цель.</p></div>
          <div><span>04</span><strong>Действие</strong><p>Кнопка заметна, конкретна и повторяется там, где это уместно.</p></div>
        </div>
      </section>

      <section className="content-section process-compact-section">
        <SectionHeading number="04" label="Процесс" title="От задачи до готового HTML" />
        <div className="compact-steps">
          <span>Бриф</span><ArrowRight aria-hidden="true" />
          <span>Прототип</span><ArrowRight aria-hidden="true" />
          <span>Дизайн</span><ArrowRight aria-hidden="true" />
          <span>Вёрстка</span><ArrowRight aria-hidden="true" />
          <span>Проверка</span>
        </div>
      </section>

      <section className="content-section content-note email-note">
        <EnvelopeSimple aria-hidden="true" />
        <div>
          <span>Для первого расчёта</span>
          <h2>Нужны цель письма, примерный объём, фирменные материалы и сервис рассылки.</h2>
          <p>Если текста ещё нет, можно начать со структуры и списка ключевых сообщений.</p>
        </div>
        <button type="button" onClick={() => onNavigate('bots')}>Задать вопрос</button>
      </section>
    </ServicePageShell>
  )
}

function localDemoReply(message) {
  const normalized = message.toLowerCase()
  const suspicious = /(игнорир|system prompt|системн.{0,8}(промпт|инструк)|jailbreak|притворись|расскажи.{0,20}(рецепт|погод|политик))/i
  const workRelated = /(сайт|лендинг|веб|дизайн|разработ|письм|рассыл|email|html|почт|telegram|телеграм|бот|mini.?app|мини.?апп|заказ|проект|услуг|портфолио|кейс|адаптив|интеграц|аналитик|домен|хостинг|seo|связ|контакт|бриф)/i

  if (suspicious.test(normalized) || !workRelated.test(normalized)) return OFF_TOPIC_REPLY
  if (/(стоим|цен|бюджет)/i.test(normalized)) return 'Стоимость зависит от формата, количества экранов или сценариев и нужных интеграций. Опишите задачу и желаемый результат, чтобы подготовить оценку без выдуманной фиксированной цены.'
  if (/(срок|быстр|долго)/i.test(normalized)) return 'Срок можно назвать после короткого брифа: важно знать объём страниц или сценариев, готовность материалов и список интеграций.'
  if (/(письм|рассыл|email|почт)/i.test(normalized)) return 'Можно разработать промо-письмо, дайджест, транзакционный шаблон или welcome-цепочку. В работу входят структура, дизайн, адаптивная HTML-вёрстка и проверка перед передачей.'
  if (/(бот|telegram|телеграм|mini.?app|мини.?апп)/i.test(normalized)) return 'Можно сделать бота-консультанта, автоматизацию заявок или Mini App: запись, каталог, личный кабинет либо программу лояльности. Состав решения зависит от процесса, который нужно упростить.'
  return 'Kazak.V разрабатывает сайты под задачу: от смысловой структуры и прототипа до адаптивной сборки, интеграций и запуска. Опишите продукт и цель сайта, и я подскажу подходящий формат.'
}

function createSessionId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID()
  const bytes = new Uint8Array(16)
  globalThis.crypto.getRandomValues(bytes)
  return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('')
}

function BotChatDemo() {
  const [messages, setMessages] = useState([
    { id: 'welcome', role: 'bot', text: 'Здравствуйте. Я консультант Kazak.V. Спросите о разработке сайтов, HTML-писем, Telegram-ботов или Mini Apps.' },
  ])
  const [draft, setDraft] = useState('')
  const [status, setStatus] = useState('idle')
  const sessionId = useMemo(createSessionId, [])
  const inputRef = useRef(null)

  const ask = async (question) => {
    const text = question.trim().slice(0, 500)
    if (!text || status === 'loading') return

    setMessages((current) => [...current, { id: createSessionId(), role: 'user', text }])
    setDraft('')
    setStatus('loading')

    try {
      let answer
      if (BOT_API_URL) {
        const controller = new AbortController()
        const timeoutId = window.setTimeout(() => controller.abort(), 15_000)
        const response = await fetch(`${BOT_API_URL}/api/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Chat-Session': sessionId,
          },
          body: JSON.stringify({ message: text }),
          signal: controller.signal,
        })
        window.clearTimeout(timeoutId)
        const payload = await response.json()
        if (!response.ok || !payload?.answer) throw new Error('Bot API is unavailable')
        answer = payload.answer
      } else {
        answer = localDemoReply(text)
      }

      setMessages((current) => [...current, { id: createSessionId(), role: 'bot', text: answer }])
      setStatus('idle')
    } catch {
      setMessages((current) => [...current, {
        id: createSessionId(),
        role: 'bot',
        text: 'Сейчас Worker недоступен. Попробуйте ещё раз позже или откройте содержание услуг на этой странице.',
      }])
      setStatus('error')
    } finally {
      window.setTimeout(() => inputRef.current?.focus(), 0)
    }
  }

  const submit = (event) => {
    event.preventDefault()
    ask(draft)
  }

  return (
    <div className="bot-demo-shell">
      <header>
        <span className="bot-avatar"><Robot weight="fill" aria-hidden="true" /></span>
        <div><strong>Kazak.V consultant</strong><small>{BOT_API_URL ? 'Cloudflare Worker' : 'локальное демо'}</small></div>
        <i className={status === 'error' ? 'is-error' : ''} aria-hidden="true" />
      </header>
      <div className="bot-demo-messages" aria-live="polite">
        {messages.map((message) => (
          <p className={`bot-demo-message is-${message.role}`} key={message.id}>{message.text}</p>
        ))}
        {status === 'loading' ? <p className="bot-demo-message is-bot is-typing">Печатает<span>...</span></p> : null}
      </div>
      <div className="bot-quick-questions" aria-label="Быстрые вопросы">
        {['Сколько стоит сайт?', 'Какие Mini Apps делаете?', 'Расскажите о погоде'].map((question) => (
          <button type="button" key={question} onClick={() => ask(question)}>{question}</button>
        ))}
      </div>
      <form onSubmit={submit}>
        <label className="visually-hidden" htmlFor="bot-question">Вопрос консультанту</label>
        <input
          ref={inputRef}
          id="bot-question"
          value={draft}
          maxLength="500"
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Напишите вопрос об услугах..."
        />
        <button type="submit" disabled={!draft.trim() || status === 'loading'} aria-label="Отправить вопрос">
          <PaperPlaneTilt weight="fill" aria-hidden="true" />
        </button>
      </form>
    </div>
  )
}

function BotsPage({ service, onBack }) {
  return (
    <ServicePageShell
      service={service}
      onBack={onBack}
      eyebrow="Telegram-боты и Mini Apps"
      title={<>Рабочий сценарий внутри <em>Telegram</em></>}
      intro="Боты отвечают, собирают данные и запускают процессы. Mini Apps добавляют полноценный интерфейс: каталог, запись, кабинет или программу лояльности. Ниже уже встроено демо консультанта Kazak.V."
      aside={<BotChatDemo />}
    >
      <section className="content-section bot-scope-section">
        <SectionHeading
          number="01"
          label="Консультант"
          title="Что умеет бот на этой странице"
          text="Один и тот же Cloudflare Worker обслуживает веб-чат и Telegram webhook."
        />
        <div className="scope-grid">
          <article><Robot aria-hidden="true" /><h3>Отвечает по услугам</h3><p>Объясняет форматы сайтов, писем, ботов и Mini Apps, процесс и необходимые вводные.</p></article>
          <article><FlowArrow aria-hidden="true" /><h3>Не уходит в другие темы</h3><p>Посторонние вопросы и попытки сменить роль останавливаются до обращения к AI-модели.</p></article>
          <article><Gauge aria-hidden="true" /><h3>Защищён от спама</h3><p>Ограничивает частоту запросов, длину сообщений и принимает Telegram webhook только с секретом.</p></article>
        </div>
      </section>

      <section className="content-section">
        <SectionHeading
          number="02"
          label="Mini Apps"
          title="Примеры приложений внутри Telegram"
          text="Это не готовые кейсы, а направления, которые можно спроектировать под реальный процесс компании."
        />
        <div className="miniapp-grid">
          {miniApps.map((app) => {
            const Icon = app.icon
            return (
              <article className="miniapp-card" key={app.title}>
                <div className="miniapp-phone" aria-hidden="true">
                  <span><Icon weight="fill" /></span>
                  <i /><i /><i />
                  <strong>{app.title}</strong>
                  <small>Открыть</small>
                </div>
                <div>
                  <Icon aria-hidden="true" />
                  <h3>{app.title}</h3>
                  <p>{app.text}</p>
                  <ul>{app.features.map((feature) => <li key={feature}>{feature}</li>)}</ul>
                </div>
              </article>
            )
          })}
        </div>
      </section>

      <section className="content-section split-section">
        <SectionHeading
          number="03"
          label="Варианты"
          title="Что ещё можно автоматизировать"
          text="Бот особенно полезен там, где сотрудники повторяют одинаковые действия."
        />
        <ul className="check-list">
          {[
            'Первичная консультация и квалификация заявки',
            'Анкеты, квизы и пошаговый сбор данных',
            'Уведомления о заказах, оплатах и событиях',
            'Интеграции с CRM, таблицами и внутренними системами',
            'Поддержка клиентов по подготовленной базе знаний',
            'Административные команды и отчёты для команды',
          ].map((item) => <li key={item}><Check weight="bold" aria-hidden="true" />{item}</li>)}
        </ul>
      </section>

      <section className="content-section content-note bot-note">
        <DeviceMobile aria-hidden="true" />
        <div>
          <span>Для оценки автоматизации</span>
          <h2>Опишите, кто пользуется ботом, что он получает на входе и какой результат должен выдать.</h2>
          <p>После этого можно разделить сценарий на команды, интеграции, данные и интерфейсы Mini App.</p>
        </div>
      </section>
    </ServicePageShell>
  )
}

export default function ServicePage({ service, onBack, onNavigate }) {
  if (service.id === 'sites') return <SitesPage service={service} onBack={onBack} onNavigate={onNavigate} />
  if (service.id === 'emails') return <EmailsPage service={service} onBack={onBack} onNavigate={onNavigate} />
  return <BotsPage service={service} onBack={onBack} />
}
